import { useEffect, useRef, useState } from "react";
import {
  Section,
  Field,
  ServerField,
  SectionData,
  EntityAvailableFields,
} from "./types";
import { getTranslation, formatTranslation, showNotification } from "@/utils";
import FormHeader from "./components/FormHeader";
import { Button, ConfirmationModal } from "@/shared-components";

type FormLayoutWrapperProp = {
  entity: string;
  allEntitiesSectionData: Record<string, SectionData[]>;
  allEntitiesFieldData: Record<string, Record<string, Field>>;
  integratedFields: Record<string, Record<string, any>>;
  enableChangeEntity: boolean;
  handleEntityChange: (entity: string) => void;
  handleSave: (
    payload: Record<string, unknown>,
    onSuccess?: () => void,
  ) => void;
  allEntitiesAvailableFieldsData: EntityAvailableFields;
  testCaseVersioningEnabled?: boolean;
};
import {
  entities,
  mapEntities,
  getKeyByValue,
  isSystemField,
  isCustomField,
  isIntegrationSection,
} from "./utils";
import FormLayout from "./FormLayout";
import { useFormLayoutLabels } from "@/contexts/translation.context";
import {
  INTEGRATION_SYSTEM_NAME,
  READ_ONLY_FIELDS,
  EXCLUDED_FIELDS,
  HIDDEN_FIELDS,
} from "@/constants";

const RUN_FIELDS_SECTION = "RUN_FIELDS_SECTION";

// The field can belong to only one of these entities at a time.
const ENTITY_GROUP = ["testset", "instance", "run"];

const FormLayoutWrapper = ({
  entity,
  allEntitiesSectionData,
  allEntitiesFieldData,
  allEntitiesAvailableFieldsData,
  integratedFields,
  handleSave,
  testCaseVersioningEnabled = false,
}: FormLayoutWrapperProp) => {
  const [availableFields, setAvailableFields] = useState<
    EntityAvailableFields | undefined
  >();
  const labels = useFormLayoutLabels();

  const [entitySections, setEntitySections] = useState<Section[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const isDirtyRef = useRef(false);
  const [selectedEntity, setSelectedEntity] = useState<string>(entity);
  const [payload, setPayload] = useState<Record<string, unknown>>();
  const [dbEntitySections, setDbEntitySections] = useState<Section[]>([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [sectionsSummary, setSectionsSummary] = useState<Section[]>([]);

  // Skip the sentinel entry pushed on mount (-2) so Cancel always leaves the page in one click.
  const navigateBack = () => {
    window.history.go(-2);
  };

  const isRunEntity = selectedEntity === "run";

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    // After a refresh the browser restores the sentinel as the current entry,
    // so pushing again would stack a second one and break navigateBack's -2.
    if (!window.history.state?.formLayoutGuard) {
      window.history.pushState({ formLayoutGuard: true }, "");
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
      }
    };

    const handlePopState = () => {
      if (isDirtyRef.current) {
        // replaceState keeps URL stable while confirm is shown;
        // sentinel is now gone so only one step back is needed.
        window.history.replaceState({ formLayoutGuard: true }, "");
        const confirmed = window.confirm(labels.confirmCancel);
        if (confirmed) {
          isDirtyRef.current = false;
          window.history.go(-1);
        }
      }
      // Not dirty: browser handles back navigation normally
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const prepareField = (field: ServerField): Field => {
    const uniq_name = field.uniq_name;

    const displayName = isSystemField(uniq_name)
      ? field.display_name
      : field.name;

    if (!Array.isArray(field.possible_entities)) {
      field.possible_entities = field.possible_entities
        ? [field.possible_entities]
        : [];
    }

    const mandatory =
      !!(allEntitiesFieldData[selectedEntity]?.[uniq_name] as any)?.[
        "mandatory?"
      ] || false;

    const position =
      allEntitiesFieldData[selectedEntity]?.[uniq_name]?.["position"] || 0;

    return {
      id: uniq_name,
      key: uniq_name,
      label: displayName,
      mandatory,
      position,
      possible_entities: isCustomField(uniq_name)
        ? // ToDo: fix typescript error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          entities.map((entity) => entity.id)
        : field.possible_entities?.map(
            (entity) => mapEntities[entity] || entity,
          ) || [],
      type: field.field_format || "text",
      section_id: field.section_id,
      hidden: HIDDEN_FIELDS?.[selectedEntity]?.fields.includes(uniq_name),
      readOnly:
        !!READ_ONLY_FIELDS?.[selectedEntity]?.fields.includes(uniq_name),
    };
  };

  const filterAndPrepareFields = (
    fields: ServerField[],
    isSystem: boolean,
  ): Record<string, Field> => {
    if (!selectedEntity) return {};
    const entityKey = getKeyByValue(mapEntities, selectedEntity);

    // Build set of valid (non-integration) section IDs for the current entity
    const validSectionIds = new Set(
      (allEntitiesSectionData[selectedEntity] || []).map((s) =>
        s.id.toString(),
      ),
    );

    return fields.reduce<Record<string, Field>>((acc, field) => {
      const isValidForEntity =
        !isSystem || field.possible_entities?.includes(entityKey || "");
      const key = field.uniq_name;
      const entityFieldData = allEntitiesFieldData[selectedEntity]?.[key];

      // A field is truly in a section only if it's attached AND has a
      // section_id pointing to an existing section. Fields that are attached
      // but have no section_id (or point to a deleted section) should still
      // appear in available fields - except for "run", where fields without
      // a section_id are rendered in the synthetic Run Details section.
      const isInRenderedSection =
        !!entityFieldData &&
        ((!!entityFieldData.section_id &&
          validSectionIds.has(entityFieldData.section_id.toString())) ||
          (isRunEntity && !entityFieldData.section_id));

      if (
        isValidForEntity &&
        !isInRenderedSection &&
        !EXCLUDED_FIELDS[selectedEntity]?.fields.includes(key)
      ) {
        acc[key] = prepareField(field);
      }
      return acc;
    }, {});
  };

  const prepareAvailableFields = () => {
    if (!selectedEntity) return;

    const systemFields = filterAndPrepareFields(
      allEntitiesAvailableFieldsData.system_fields.filter(
        (field) => !integratedFields?.[field.uniq_name]?.[entity],
      ),
      true,
    );

    const customFields = filterAndPrepareFields(
      allEntitiesAvailableFieldsData.custom_fields.filter(
        (field) => !integratedFields?.[field.uniq_name]?.[entity],
      ),

      false,
    );

    if (ENTITY_GROUP.includes(selectedEntity as string)) {
      const targets = ENTITY_GROUP.filter((e) => e !== selectedEntity);
      const allAvailable = [
        ...Object.values(systemFields),
        ...Object.values(customFields),
      ];

      allAvailable.forEach((field) => {
        const usedByOtherEntity = targets.some(
          (target) => !!allEntitiesFieldData[target]?.[field.id],
        );
        if (usedByOtherEntity) {
          field.readOnly = true;
        }
      });
    }

    setAvailableFields({
      // ToDo: fix typescript error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      systemFields: systemFields,
      customFields: customFields,
    });
  };

  useEffect(() => {
    setSelectedEntity(entity);
  }, [entity]);

  useEffect(() => {
    if (
      !selectedEntity ||
      !allEntitiesAvailableFieldsData ||
      !allEntitiesFieldData
    )
      return;
    prepareAvailableFields();
  }, [selectedEntity, allEntitiesFieldData]); // Add shouldReload to dependencies

  useEffect(() => {
    if (
      !selectedEntity ||
      !allEntitiesAvailableFieldsData ||
      !allEntitiesFieldData
    )
      return;
    setIsDirty(false);
    prepareEntitySections();
  }, [
    availableFields,
    selectedEntity,
    allEntitiesFieldData,
    allEntitiesSectionData,
  ]);

  const handleDataChange = (
    payload: Record<string, unknown>,
    newSectionsState: Section[],
  ) => {
    const isSectionsChanged =
      JSON.stringify(newSectionsState) !== JSON.stringify(dbEntitySections);
    isDirtyRef.current = isSectionsChanged;
    setIsDirty(isSectionsChanged);
    setSectionsSummary(newSectionsState);
    if (isSectionsChanged) {
      setPayload(payload);
    }
  };

  const handleCancel = () => {
    if (!isDirty) {
      navigateBack();
    } else {
      setShowCancelModal(true);
    }
  };

  const handleCancelConfirm = () => {
    setShowCancelModal(false);
    isDirtyRef.current = false;
    navigateBack();
  };

  const handleCancelDismiss = () => {
    setShowCancelModal(false);
  };

  const isPayloadValid = (): boolean => {
    const messages: string[] = [];
    // ToDo: fix typescript error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    for (const section of payload.sections) {
      // Removed validation for empty fields to allow sections without fields
      if (
        !section.label.trim() ||
        section.label === labels.newSectionPlaceholder
      ) {
        showNotification("error", labels.pleaseFillSectionLabel);
        messages.push(labels.pleaseFillSectionLabel);
      }
    }

    return messages.length === 0;
  };

  const onSaveClicked = async (closeAfterSave = false) => {
    if (!isPayloadValid()) {
      return;
    }

    try {
      if (!payload) return;
      handleSave(
        payload,
        closeAfterSave
          ? () => {
              isDirtyRef.current = false;
              navigateBack();
            }
          : undefined,
      );
    } catch (error) {
      console.error(`FormLayoutWrapper->onSaveClicked->error`, error);
    }
  };

  interface GroupedFields {
    [sectionId: string]: Field[];
  }

  const getField = (key: string): Field => {
    const systemField = allEntitiesAvailableFieldsData.system_fields.find(
      (f) => f.uniq_name === key,
    );
    const customField = allEntitiesAvailableFieldsData.custom_fields.find(
      (f) => f.uniq_name === key,
    );
    // ToDo: fix typescript error
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return systemField || customField;
  };

  const prepareEntitySections = () => {
    const entitySectionsData = allEntitiesSectionData[selectedEntity];

    const integrationSection = entitySectionsData?.find(isIntegrationSection);

    // Create a map to track all sections, including those without fields
    const entitySections: Record<string, SectionData> = {};
    // Add all sections from the database to the map
    entitySectionsData?.forEach((section) => {
      if (!isIntegrationSection(section)) {
        entitySections[section.id.toString()] = section;
      }
    });

    const entityFields = allEntitiesFieldData[selectedEntity] ?? {};
    const groupedFields = Object.entries(entityFields).reduce<GroupedFields>(
      (acc: GroupedFields, [key, field]) => {
        if (EXCLUDED_FIELDS[selectedEntity]?.fields.includes(key)) {
          return acc;
        }
        const typedField = {
          ...getField(key),
          section_id: field.section_id,
        };

        let sectionId = typedField.section_id?.toString();

        // For run entity, keep fields without section_id in a dedicated UI-only section
        if (!sectionId && isRunEntity) {
          sectionId = RUN_FIELDS_SECTION;
        }

        if (!sectionId) {
          return acc;
        }

        if (!acc[sectionId]) {
          acc[sectionId] = [];
        }
        acc[sectionId].push(typedField);
        return acc;
      },
      {},
    );

    const sortFields = (fields: Field[]) =>
      fields
        // ToDo: fix typescript error
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .map((field) => prepareField(field))
        .sort((a, b) => a.position - b.position);

    const generalSectionId = allEntitiesSectionData[selectedEntity]?.find(
      (s) => s.system_section === "general_info",
    )?.id;

    // should not be possible but just in case no fields are found
    // create general section containing no fields
    if (generalSectionId && !groupedFields[generalSectionId]) {
      groupedFields[generalSectionId] = [];
    }

    // Create sections array from all sections, including those without fields
    const sectionsArray: Section[] = Object.keys(entitySections)
      .map((sectionId) => {
        const sectionData = prepeareSectionData(sectionId);
        if (!sectionData) return null;

        // Get fields for this section if they exist, otherwise use empty array
        const sortedFields = sortFields(groupedFields[sectionId] || []);
        const isDescriptionSection =
          sectionData.label === labels.descriptionLabel;

        return {
          ...sectionData,
          disabled: isDescriptionSection ? true : false,
          row: sectionData.row || 0,
          id: sectionData.id,
          isSystem: isDescriptionSection ? true : sectionData.isSystem,
          column: sectionData.column || 0,
          label: sectionData.label,
          fields: sortedFields,
        } as Section;
      })
      .filter((section): section is Section => section !== null);

    if (isRunEntity) {
      const runFields = sortFields(groupedFields[RUN_FIELDS_SECTION] || []);

      sectionsArray.push({
        id: RUN_FIELDS_SECTION,
        label: getTranslation("run.details", "Run Details"),
        row: 0,
        column: 1,
        disabled: false,
        isSystem: true,
        fields: runFields,
      });
    }

    sectionsArray.push({
      id: "DESCRIPTION",
      label: labels.descriptionLabel,
      row: 0,
      column: 0,
      disabled: true,
      isSystem: true,
      fields: [],
    });

    if (
      (selectedEntity === "test" || selectedEntity === "instance") &&
      testCaseVersioningEnabled
    ) {
      sectionsArray.push({
        id: "TEST_VERSIONING",
        label: getTranslation("test.versioning.title", "Test Case Versioning"),
        row: 0,
        column: 1,
        disabled: true,
        isSystem: true,
        fields: [],
      });
    }

    if (selectedEntity === "instance") {
      sectionsArray.push({
        id: "STEP_PARAMETERS",
        label: getTranslation("instance.step-parameters", "Step Parameters"),
        row: 0,
        column: 1,
        disabled: true,
        isSystem: true,
        fields: [],
      });
    }

    if (integrationSection) {
      const integrationFields = sortFields(
        groupedFields[integrationSection.id.toString()] || [],
      );

      sectionsArray.push({
        id: integrationSection.id.toString(),
        system_section: INTEGRATION_SYSTEM_NAME,
        label: integrationSection.label || "",
        row: integrationSection.row_position || 0,
        column: integrationSection.column_position || 0,
        disabled: true,
        isSystem: true,
        fields: integrationFields,
      });
    }

    setEntitySections(sectionsArray);
    setDbEntitySections(sectionsArray);
    setSectionsSummary(sectionsArray);
  };

  const prepeareSectionData = (sectionId: string): Section | null => {
    if (!selectedEntity || !allEntitiesSectionData[selectedEntity]) return null;

    const sectionData = allEntitiesSectionData[selectedEntity]?.find(
      (section) => section.id.toString() === sectionId,
    );

    if (!sectionData) return null;

    return {
      id: sectionId,
      label: sectionData.label || "",
      column: sectionData.column_position || 0,
      row: sectionData.row_position || 0,
      disabled: sectionData.disabled || false,
      isSystem: !!sectionData.system_section,
      fields: [],
    };
  };

  const totalFieldsCount = sectionsSummary.reduce((sum, section) => {
    if (section.id === "DESCRIPTION") return sum + 1;
    return sum + (section.fields?.length || 0);
  }, 0);

  return (
    <section className="form-settings-page">
      <FormHeader selectedEntity={selectedEntity} />
      {selectedEntity && availableFields && entitySections && (
        <>
          <FormLayout
            entity={selectedEntity}
            // ToDo: fix typescript error
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            entityAvailableFields={availableFields}
            entitySections={entitySections}
            handleDataChange={handleDataChange}
          />

          <div className="form-footer">
            <div className="footer-summary">
              {formatTranslation(
                "sections.labels.footer-summary",
                `${totalFieldsCount} fields across ${sectionsSummary.length} sections · drag to reorder, or drag a field back to Available Fields to remove it`,
                {
                  fieldCount: totalFieldsCount,
                  sectionCount: sectionsSummary.length,
                },
              )}
            </div>
            <div className="form-actions">
              <Button
                type="secondary"
                onClick={handleCancel}
                text={getTranslation("generic.button.cancel", "Cancel")}
              ></Button>
              <Button
                type="secondary"
                disabled={!isDirty}
                onClick={() => onSaveClicked()}
                text={getTranslation("generic.button.save", "Save")}
              ></Button>
              <Button
                disabled={!isDirty}
                onClick={() => onSaveClicked(true)}
                text={getTranslation(
                  "generic.button.save-and-close",
                  "Save and Close",
                )}
              ></Button>
            </div>
          </div>
        </>
      )}

      <ConfirmationModal
        show={showCancelModal}
        title={getTranslation(
          "sections.notify.confirm-cancel-title",
          "Unsaved Changes",
        )}
        body={labels.confirmCancel}
        yesLabel={getTranslation(
          "global-fields.migration.confirmation.approve",
          "Approve",
        )}
        noLabel={getTranslation("view-editor.cancel", "Cancel")}
        onYes={handleCancelConfirm}
        onNo={handleCancelDismiss}
      />
    </section>
  );
};

export { FormLayoutWrapper };
